/* eslint-disable no-console -- Intentional console logging service */
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Environment-aware logging service.
 * Suppresses debug/info logs in production while always logging warnings and errors.
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly isProduction = environment.production;
  private readonly debugEnabled = environment.messages?.debug ?? false;

  /**
   * Log debug information. Only outputs in non-production when debug is enabled.
   */
  debug(message: string, ...args: unknown[]): void {
    if (!this.isProduction && this.debugEnabled) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Log general information. Only outputs in non-production.
   */
  info(message: string, ...args: unknown[]): void {
    if (!this.isProduction) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Log warnings. Always outputs (useful for deprecation notices, etc.).
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(`[WARN] ${message}`, ...args);
  }

  /**
   * Log errors. Always outputs.
   */
  error(message: string, error?: unknown): void {
    console.error(`[ERROR] ${message}`, error ?? '');
  }

  /**
   * Log a group of related messages. Only outputs in non-production.
   */
  group(label: string, fn: () => void): void {
    if (!this.isProduction) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  }

  /**
   * Log tabular data. Only outputs in non-production.
   */
  table(data: unknown): void {
    if (!this.isProduction) {
      console.table(data);
    }
  }
}
